import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Link,
  Box,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Dialog,
  DialogContent,
} from '@mui/material';
import ClearRounded from '@mui/icons-material/ClearRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import MapRounded from '@mui/icons-material/MapRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import LocationMap from './LocationMap';
import LocationOnRounded from '@mui/icons-material/LocationOnRounded';
import { calculateTopCompetitors } from './utils/competitorAnalysis';

import './App.css';
import { Collapse } from '@mui/material';

function App() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]); 
  const [selected, setSelected] = useState(null); 
  const [details, setDetails] = useState(null); 
  const [photoUrl, setPhotoUrl] = useState(''); 
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null); 
  const [isCompanyLinked, setIsCompanyLinked] = useState(false); 
  const [locationRanks, setLocationRanks] = useState([]); 
  const [searchKeyword, setSearchKeyword] = useState('');
  const [keywordVolume, setKeywordVolume] = useState(null);
  const [loadingVolume, setLoadingVolume] = useState(false);
  const [searchKeywordInput, setSearchKeywordInput] = useState('');
  const [topCompetitors, setTopCompetitors] = useState([]);
  const [keywordsHistory, setKeywordsHistory] = useState([]);
  const [isLoadingKeyword, setIsLoadingKeyword] = useState(false);
  const [cachedResults, setCachedResults] = useState({});
  const [keywordError, setKeywordError] = useState(null);
  const [savedCompanies, setSavedCompanies] = useState(() => {
    const saved = localStorage.getItem('savedCompanies');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentCompany, setCurrentCompany] = useState(null);
  const [keywordHistory, setKeywordHistory] = useState({});
  const [searchMode, setSearchMode] = useState('company');
  const [showLocationMode, setShowLocationMode] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Multi-Keyword State Management
  const [multiKeywordInput, setMultiKeywordInput] = useState('');
  const [parsedKeywords, setParsedKeywords] = useState([]);
  const [activeKeywords, setActiveKeywords] = useState([]); // { keyword, status: 'pending' | 'analyzing' | 'complete' | 'error', result, progress }
  const [selectedKeywordForView, setSelectedKeywordForView] = useState(null);
  const [isMultiKeywordMode, setIsMultiKeywordMode] = useState(false);
  const [simultaneousKeywords, setSimultaneousKeywords] = useState(3);
  const [showCTAForm, setShowCTAForm] = useState(false);
  const [ctaFormData, setCtaFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: ''
  });
  const [isSubmittingCTA, setIsSubmittingCTA] = useState(false);
  const [ctaSubmitSuccess, setCtaSubmitSuccess] = useState(false);

  // Exit-Intent State Management
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);

  useEffect(() => {
    if (keywordsHistory.includes(searchKeywordInput.trim()) && searchKeywordInput.trim() !== '') {
      setSearchKeywordInput('');
    }
  }, [keywordsHistory, searchKeywordInput]);
  
  useEffect(() => {
    localStorage.setItem('savedCompanies', JSON.stringify(savedCompanies));
  }, [savedCompanies]);
  
  useEffect(() => {
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    if (activeCompanyId) {
      const company = savedCompanies.find(c => c.id === activeCompanyId);
      if (company) {
        setCurrentCompany(company);
        setDetails(company.details);
        if (company.cachedResults) {
          setCachedResults(company.cachedResults);
        }
        setIsCompanyLinked(true);
      }
    }
  }, [savedCompanies]);

  // Exit-Intent Detection: Show modal when user tries to leave after analysis
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Check if rankings show potential for improvement (any rank > 3)
      const needsImprovement = locationRanks.some(location => location.rank > 3);

      // Only trigger if:
      // 1. Mouse leaves from top of viewport
      // 2. Rankings have been analyzed
      // 3. Rankings show potential for improvement
      // 4. Modal hasn't been shown yet this session
      if (
        e.clientY <= 0 &&
        locationRanks.length > 0 &&
        needsImprovement &&
        !exitIntentShown
      ) {
        setShowExitModal(true);
        setExitIntentShown(true);
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [locationRanks, exitIntentShown]);

  const saveCompany = (companyDetails) => {
    const companyId = companyDetails.place_id;
    const existingCompany = savedCompanies.find(company => company.id === companyId);
    
    if (existingCompany) {
      const updatedCompany = {
        ...existingCompany,
        details: companyDetails,
        lastUpdated: new Date().toISOString()
      };
      
      setSavedCompanies(savedCompanies.map(company => 
        company.id === companyId ? updatedCompany : company
      ));
      setCurrentCompany(updatedCompany);
    } else {
      const newCompany = {
        id: companyId,
        details: companyDetails,
        name: companyDetails.name,
        address: companyDetails.formatted_address,
        cachedResults: cachedResults,
        keywordHistory: keywordHistory,
        competitors: {},
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      
      setSavedCompanies([...savedCompanies, newCompany]);
      setCurrentCompany(newCompany);
    }
    
    localStorage.setItem('activeCompanyId', companyId);
  };
  
  const deleteCompany = (companyId) => {
    const updatedCompanies = savedCompanies.filter(company => company.id !== companyId);
    setSavedCompanies(updatedCompanies);
    
    if (currentCompany && currentCompany.id === companyId) {
      setCurrentCompany(null);
      localStorage.removeItem('activeCompanyId');
      handleReset();
    }
  };
  
  const generateConcentricPins = (
    centerLat,
    centerLng,
    radiiKm = [7.5, 15],
    pointsPerRing = [6, 12]
  ) => {
    const pins = [];
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;
    const earthRadius = 6371;
    radiiKm.forEach((radius, ringIndex) => {
      const points = pointsPerRing[ringIndex];
      for (let i = 0; i < points; i++) {
        const bearing = (2 * Math.PI * i) / points;
        const lat1 = toRad(centerLat);
        const lng1 = toRad(centerLng);
        const distance = radius / earthRadius;
        const lat2 = Math.asin(
          Math.sin(lat1) * Math.cos(distance) +
            Math.cos(lat1) * Math.sin(distance) * Math.cos(bearing)
        );
        const lng2 =
          lng1 +
          Math.atan2(
            Math.sin(bearing) * Math.sin(distance) * Math.cos(lat1),
            Math.cos(distance) - Math.sin(lat1) * Math.sin(lat2)
          );
        const finalLat = toDeg(lat2);
        const finalLng = toDeg(lng2);
        pins.push({
          lat: finalLat,
          lng: finalLng,
          locationName: '',
          rank: 20,
          color: theme.palette.error.main,
          companies: []
        });
      }
    });
    return pins;
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim() === '') {
      setResults([]);
      setSelected(null);
      setDetails(null);
      setPhotoUrl('');
      setError(null);
      setIsCompanyLinked(false);
      setLocationRanks([]);
      return;
    }
    try {
      const response = await axios.get(`/api/search?query=${value}`);
      setResults(response.data.predictions || []);
    } catch (error) {
      console.error('Fehler beim Abrufen der Daten:', error);
      setError('Fehler bei der Suche: ' + error.message);
    }
  };

  const handleReset = () => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setDetails(null);
    setPhotoUrl('');
    setError(null);
    setIsCompanyLinked(false);
    setLocationRanks([]);
    setKeywordError(null);
    setSearchMode('company');
    setShowLocationMode(false);
    setLocationInput('');
  };

  const handleLocationInputChange = async (e) => {
    const value = e.target.value;
    setLocationInput(value);

    if (value.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    try {
      // Use Google Places Autocomplete for geocoding/regions only
      const response = await axios.get(`/api/geocode-autocomplete?input=${encodeURIComponent(value)}`);
      if (response.data.predictions) {
        setLocationSuggestions(response.data.predictions);
        setShowLocationSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    }
  };

  const handleSelectLocationSuggestion = async (suggestion) => {
    setLocationInput(suggestion.description);
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
    setLoadingLocation(true);
    setError(null);

    try {
      // Use Place Details API to get exact coordinates
      const placeDetailsResponse = await axios.get(`/api/place-details-simple?place_id=${suggestion.place_id}`);

      console.log('Place Details Response:', placeDetailsResponse.data);

      if (!placeDetailsResponse.data || !placeDetailsResponse.data.geometry) {
        throw new Error('Keine gültige Adresse gefunden');
      }

      const location = placeDetailsResponse.data.geometry.location;
      const formattedAddress = placeDetailsResponse.data.formatted_address || suggestion.description;

      const mockDetails = {
        place_id: `location_${Date.now()}`,
        name: formattedAddress,
        formatted_address: formattedAddress,
        geometry: {
          location: {
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng)
          }
        },
        isLocationMode: true
      };

      console.log('Created mock details:', mockDetails);

      setDetails(mockDetails);
      setIsCompanyLinked(true);
      setSearchMode('location');
      setQuery(formattedAddress);
      setPhotoUrl('https://placehold.co/300x140?text=Standort-Modus');

    } catch (error) {
      console.error('Fehler beim Geocoding:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unbekannter Fehler';
      setError('Standort konnte nicht gefunden werden: ' + errorMessage);
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleSelectFromList = async (item) => {
    setSelected(item);
    setResults([]);
    setDetails(null);
    setPhotoUrl('');
    setError(null);
    setLoadingDetails(true);
    try {
      const response = await axios.get(`/api/details?place_id=${item.place_id}`);
      const placeDetails = response.data.result;
      setDetails(placeDetails);
      fetchPhoto(placeDetails);
      linkCompany(placeDetails);

      // Track company search in Zapier (non-blocking)
      // Only send if we have a valid company name (not just a location)
      const companyName = placeDetails.name || item.name;
      if (companyName && !placeDetails.isLocationMode) {
        console.log('Tracking company search:', companyName);
        axios.post('/api/company-search', {
          companyName: companyName,
          placeId: placeDetails.place_id || item.place_id,
          address: placeDetails.formatted_address || item.formatted_address,
          keyword: searchKeyword || null,
          rankings: locationRanks && locationRanks.length > 0 ? locationRanks : null,
          timestamp: new Date().toISOString()
        }).catch(err => console.log('Company search tracking failed:', err.message));
      } else {
        console.log('Skipping tracking - no company name or location mode active');
      }

      const existingCompany = savedCompanies.find(company => company.id === placeDetails.place_id);
      if (existingCompany) {
        setCurrentCompany(existingCompany);
        if (existingCompany.cachedResults) {
          setCachedResults(existingCompany.cachedResults);
        }
        if (existingCompany.keywordHistory) {
          setKeywordHistory(existingCompany.keywordHistory);
        }
        localStorage.setItem('activeCompanyId', placeDetails.place_id);
      } else {
        saveCompany(placeDetails);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Place Details:', error);
      setError('Fehler beim Laden der Details: ' + error.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const linkCompany = (placeDetails) => {
    setIsCompanyLinked(true);
    setQuery(`${placeDetails.name}, ${placeDetails.formatted_address}`);
    saveCompany(placeDetails);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && selected && !isCompanyLinked) {
      linkCompany(details);
    }
  };

  const fetchPhoto = async (details) => {
    if (details.photos && details.photos.length > 0) {
      const photoReference = details.photos[0].photo_reference;
      if (!photoReference) {
        setPhotoUrl('https://placehold.co/300x140?text=Kein+Bild+verfügbar');
        setError('Keine photo_reference verfügbar für dieses Foto.');
        return;
      }
      try {
        const response = await axios.get(
          `/api/photo?photoreference=${encodeURIComponent(photoReference)}`,
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(response.data);
        setPhotoUrl(url);
      } catch (error) {
        setPhotoUrl('https://placehold.co/300x140?text=Kein+Bild+verfügbar');
        setError('Fehler beim Laden des Bildes: ' + error.message);
      }
    } else {
      setPhotoUrl('https://placehold.co/300x140?text=Kein+Bild+verfügbar');
      setError('Keine Fotos verfügbar für diesen Ort.');
    }
  };

  const fetchLocationRanks = async (placeDetails, keyword = searchKeyword) => {
    if (!placeDetails?.geometry?.location) return;
    if (!keyword || keyword.trim() === '') {
      setIsLoadingKeyword(false);
      return;
    }
    setIsLoadingKeyword(true);
    setKeywordError(null);
    setSearchKeyword(keyword);
    const centerLat = placeDetails.geometry.location.lat;
    const centerLng = placeDetails.geometry.location.lng;
    const cacheKey = `${placeDetails.place_id}_${keyword}`;

    // Check for cached results and use them if available
    if (cachedResults[cacheKey]) {
      const cachedData = cachedResults[cacheKey];
      // Normalize: extract ranks array if cached data is an object
      const ranksArray = Array.isArray(cachedData) ? cachedData : (cachedData?.ranks || []);
      setLocationRanks(ranksArray);
      const competitors = calculateTopCompetitors(ranksArray, placeDetails.place_id);
      setTopCompetitors(competitors);
      fetchKeywordVolume(keyword, placeDetails);
      setIsLoadingKeyword(false);
      return;
    }

    const concentricPins = generateConcentricPins(centerLat, centerLng);
    const centerPin = {
      lat: centerLat,
      lng: centerLng,
      locationName: '',
      rank: 20,
      color: theme.palette.error.main,
      companies: []
    };
    
    try {
      const analysisResponse = await axios.post('/api/analyze-keyword', {
        placeId: placeDetails.place_id,
        lat: centerLat,
        lng: centerLng,
        keyword: keyword,
        businessName: placeDetails.name
      });
      
      if (!analysisResponse.data.success) {
        throw new Error(analysisResponse.data.error || 'SerpAPI Analyse fehlgeschlagen');
      }
      
      const analysisResult = analysisResponse.data.data;
      
      const ranks = await Promise.all(analysisResult.testLocations.map(async (location) => {
        const locationName = await fetchLocationName(location.lat, location.lng) || location.locationName;
        return {
          lat: location.lat,
          lng: location.lng,
          rank: location.rank || 20,
          color: getColorForRank(location.rank || 20),
          locationName,
          companies: location.companies || location.competitors || [],
          keyword,
          matchMethod: location.matchMethod
        };
      }));
      
      setCachedResults(prev => ({
        ...prev,
        [cacheKey]: ranks
      }));
      
      if (!keywordsHistory.includes(keyword)) {
        setKeywordsHistory(prev => [...prev, keyword]);
      }

      setLocationRanks(ranks);

      const competitors = calculateTopCompetitors(ranks, placeDetails.place_id);
      setTopCompetitors(competitors);
      
      fetchKeywordVolume(keyword, placeDetails);
    } catch (error) {
      setKeywordError('Fehler beim Laden der Rankings: ' + error.message);
    } finally {
      setIsLoadingKeyword(false);
    }
  };

  const fetchLocationName = async (lat, lng) => {
    try {
      const apiKey = process.env.GOOGLE_API_KEY;
      if (!apiKey) {
        return 'Unbekannter Ort';
      }
      const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
      const results = response.data.results;
      if (!results || results.length === 0) {
        return 'Unbekannter Ort';
      }
      const addressComponents = results[0].address_components;
      let locationName = 'Unbekannter Ort';
      const locality = addressComponents.find(comp => comp.types.includes('locality'))?.long_name;
      if (locality) {
        locationName = locality;
      } else {
        const administrativeArea = addressComponents.find(comp => comp.types.includes('administrative_area_level_3') || comp.types.includes('sublocality'))?.long_name;
        if (administrativeArea) {
          locationName = administrativeArea;
        } else {
          const postalCode = addressComponents.find(comp => comp.types.includes('postal_code'))?.long_name;
          if (postalCode) {
            locationName = `PLZ ${postalCode}`;
          } else {
            const country = addressComponents.find(comp => comp.types.includes('country'))?.long_name;
            const region = addressComponents.find(comp => comp.types.includes('administrative_area_level_1'))?.long_name;
            locationName = `${region || ''}, ${country || ''}`.trim() || 'Unbekannter Ort';
          }
        }
      }
      return locationName;
    } catch (error) {
      return 'Unbekannter Ort';
    }
  };

  const fetchKeywordVolume = async (keyword, placeDetails) => {
    if (!keyword || keyword.trim() === '') {
      setLoadingVolume(false);
      setKeywordVolume(null);
      return;
    }
    setLoadingVolume(true);
    try {
      const addressComponents = placeDetails?.result?.address_components || placeDetails?.address_components;
      const formattedAddress = placeDetails?.result?.formatted_address || placeDetails?.formatted_address || '';
      let city = extractCityFromAddressComponents(addressComponents);
      if (!city) {
        city = extractCityFromFormattedAddress(formattedAddress);
      }
      if (!city) {
        city = 'Unbekannte Stadt';
      }
      const countryCode = extractCountryFromPlaceDetails(placeDetails);
      let lat = placeDetails?.result?.geometry?.location?.lat || placeDetails?.geometry?.location?.lat;
      let lng = placeDetails?.result?.geometry?.location?.lng || placeDetails?.geometry?.location?.lng;
      
      let url = `/api/keyword-volume?keyword=${encodeURIComponent(keyword)}&city=${encodeURIComponent(city)}&country=${countryCode}`;
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        url += `&lat=${lat}&lng=${lng}`;
      }
      const response = await axios.get(url);
      setKeywordVolume(response.data);
      const cacheKey = `${placeDetails.place_id}_${keyword}`;
      setCachedResults(prev => {
        const existingData = prev[cacheKey];
        if (Array.isArray(existingData)) {
          return {
            ...prev,
            [cacheKey]: {
              ranks: existingData,
              volume: response.data
            }
          };
        } else if (existingData?.ranks) {
          return {
            ...prev,
            [cacheKey]: {
              ...existingData,
              volume: response.data
            }
          };
        } else {
          return {
            ...prev,
            [cacheKey]: {
              ranks: [],
              volume: response.data
            }
          };
        }
      });
    } catch (error) {
      setKeywordVolume(null);
    } finally {
      setLoadingVolume(false);
    }
  };

  const extractCityFromAddressComponents = (addressComponents) => {
    if (!addressComponents || !Array.isArray(addressComponents)) {
      return null;
    }
    const cityComponent = addressComponents.find(component => 
      component.types && (
        component.types.includes('locality') || 
        component.types.includes('administrative_area_level_1') ||
        component.types.includes('sublocality')
      )
    );
    if (cityComponent?.long_name) {
      return cityComponent.long_name;
    }
    return null;
  };

  const extractCityFromFormattedAddress = (formattedAddress) => {
    if (!formattedAddress) return null;
    const parts = formattedAddress.split(',');
    for (const part of parts) {
      const trimmed = part.trim();
      const match = trimmed.match(/^\d{4,5}\s+(.+)$/);
      if (match) {
        const city = match[1].trim();
        return city;
      }
    }
    return null;
  };

  const extractCountryFromPlaceDetails = (placeDetails) => {
    const addressComponents = placeDetails?.result?.address_components || placeDetails?.address_components;
    if (addressComponents && Array.isArray(addressComponents)) {
      const countryComponent = addressComponents.find(component => 
        component.types && component.types.includes('country')
      );
      if (countryComponent?.short_name) {
        const countryCode = countryComponent.short_name;
        const supportedCountries = ['DE', 'AT', 'CH', 'BE', 'NL'];
        if (supportedCountries.includes(countryCode)) {
          return countryCode;
        }
      }
    }
    const formattedAddress = placeDetails?.result?.formatted_address || placeDetails?.formatted_address || '';
    if (formattedAddress) {
      const address = formattedAddress.toLowerCase();
      if (address.includes('switzerland') || address.includes('schweiz')) {
        return 'CH';
      }
      if (address.includes('austria') || address.includes('österreich')) {
        return 'AT';
      }
      if (address.includes('belgium') || address.includes('belgien')) {
        return 'BE';
      }
      if (address.includes('netherlands') || address.includes('niederlande') || address.includes('holland')) {
        return 'NL';
      }
    }
    return 'DE';
  };

  // Moved below theme hook

  // Multi-Keyword Parser - Parse comma/semicolon/newline separated keywords
  const parseKeywordsFromInput = (input) => {
    if (!input || input.trim() === '') return [];

    // Split by comma, semicolon, or newline
    const keywords = input
      .split(/[,;\n]+/)
      .map(k => k.trim())
      .filter(k => k.length > 0);

    // Remove duplicates
    return [...new Set(keywords)];
  };

  // Update parsed keywords when input changes
  useEffect(() => {
    const parsed = parseKeywordsFromInput(multiKeywordInput);
    setParsedKeywords(parsed);
  }, [multiKeywordInput]);

  // Multi-Keyword Batch Analysis Function
  const analyzeMultipleKeywords = async () => {
    if (!details || parsedKeywords.length === 0) return;

    console.log(`🎯 Starting multi-keyword analysis for ${parsedKeywords.length} keywords`);

    // Initialize active keywords with 'pending' status
    const initialKeywords = parsedKeywords.map(keyword => ({
      keyword,
      status: 'pending',
      result: null,
      progress: 0,
      error: null
    }));

    setActiveKeywords(initialKeywords);
    setIsMultiKeywordMode(true);

    try {
      // Call batch API
      const response = await axios.post('/api/analyze-keywords-batch', {
        placeId: details.place_id,
        lat: details.geometry.location.lat,
        lng: details.geometry.location.lng,
        businessName: details.name,
        keywords: parsedKeywords,
        batchSize: simultaneousKeywords
      });

      if (response.data.success) {
        const results = response.data.data.results;

        // Update activeKeywords with results
        const updatedKeywords = results.map(result => ({
          keyword: result.keyword,
          status: result.status,
          result: result.analysisResult,
          progress: 100,
          error: result.error
        }));

        setActiveKeywords(updatedKeywords);

        // Add successful keywords to history
        results.forEach(result => {
          if (result.status === 'complete' && !keywordsHistory.includes(result.keyword)) {
            setKeywordsHistory(prev => [...prev, result.keyword]);
          }
        });

        // Set first successful keyword as selected for viewing
        const firstSuccess = results.find(r => r.status === 'complete');
        if (firstSuccess && firstSuccess.analysisResult) {
          setSelectedKeywordForView(firstSuccess.keyword);
          displayKeywordResults(firstSuccess.keyword, firstSuccess.analysisResult);
        }

        console.log(`✅ Multi-keyword analysis complete: ${results.length} keywords processed`);
      }
    } catch (error) {
      console.error('❌ Multi-keyword analysis failed:', error);
      setKeywordError('Fehler bei der Multi-Keyword Analyse: ' + error.message);

      // Mark all as error
      setActiveKeywords(prev => prev.map(kw => ({
        ...kw,
        status: 'error',
        error: error.message
      })));
    }
  };

  // Display results for a specific keyword
  const displayKeywordResults = async (keyword, analysisResult) => {
    if (!analysisResult || !analysisResult.testLocations) return;

    setSearchKeyword(keyword);
    setSelectedKeywordForView(keyword);

    // Convert analysisResult to locationRanks format
    const ranks = await Promise.all(analysisResult.testLocations.map(async (location) => {
      const locationName = await fetchLocationName(location.lat, location.lng) || location.locationName;
      return {
        lat: location.lat,
        lng: location.lng,
        rank: location.rank || 20,
        color: getColorForRank(location.rank || 20),
        locationName,
        companies: location.companies || location.competitors || [],
        keyword,
        matchMethod: location.matchMethod
      };
    }));

    setLocationRanks(ranks);

    const competitors = calculateTopCompetitors(ranks, details.place_id);
    setTopCompetitors(competitors);

    fetchKeywordVolume(keyword, details);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getColorForRank = (rank) => {
    if (rank <= 3) return theme.palette.success.main;
    if (rank <= 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Check if rankings need improvement (not all green)
  const hasImprovableRankings = () => {
    if (!locationRanks || locationRanks.length === 0) return false;
    return locationRanks.some(location => location.rank > 3);
  };

  // Submit CTA form via webhook
  const handleCTASubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCTA(true);

    try {
      // Proxy through backend to avoid CORS issues
      const webhookURL = '/api/cta-submission';

      const payload = {
        ...ctaFormData,
        keyword: searchKeyword,
        companyName: details?.name || '',
        placeId: details?.place_id || '',
        timestamp: new Date().toISOString(),
        rankings: locationRanks
      };

      await axios.post(webhookURL, payload);

      setCtaSubmitSuccess(true);
      setCtaFormData({ name: '', company: '', phone: '', email: '' });

      // Hide form after 3 seconds
      setTimeout(() => {
        setShowCTAForm(false);
        setCtaSubmitSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('CTA submission error:', error);
      alert('Es gab einen Fehler beim Absenden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmittingCTA(false);
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ pt: 5, pb: 8 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 500, 
              color: theme.palette.text.primary,
              letterSpacing: '-0.02em',
              mb: 2.5
            }}
          >
            <span className="gradient-text">Business Ranking-Analyse</span>
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto', 
              mb: 4, 
              fontWeight: 400,
              color: theme.palette.text.secondary
            }}
          >
            Analysieren Sie Ihre Positionierung in lokalen Suchergebnissen und optimieren Sie Ihre Sichtbarkeit auf Google Maps.
          </Typography>
        </Box>

        <Paper 
          elevation={2} 
          sx={{ 
            p: 3.5, 
            mb: 5, 
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.04)',
            background: 'white',
            overflow: 'hidden',
            position: 'relative'
          }}
          className="hover-scale"
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  mb: 2,
                  mt: 1
                }}
              >
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'white'
                  }}
                >
                  <InfoRounded sx={{ fontSize: 20, fontWeight: 300 }} />
                </Box>
                <span>Unternehmensprofil finden</span>
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.text.secondary, 
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Geben Sie den Namen Ihres Unternehmens ein, um es mit Google Maps zu verknüpfen und Rankings zu analysieren.
              </Typography>
              
              <TextField
                fullWidth
                variant="outlined"
                value={query}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                placeholder="Unternehmensname eingeben..."
                disabled={searchMode === 'location'}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    borderColor: 'primary.main',
                    boxShadow: isCompanyLinked ? '0 0 0 3px rgba(16, 185, 129, 0.1)' : '0 4px 10px rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      boxShadow: isCompanyLinked ? '0 0 0 3px rgba(16, 185, 129, 0.15)' : '0 6px 20px rgba(0, 0, 0, 0.08)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: isCompanyLinked ? 'success.main' : 'primary.main',
                      borderWidth: '1.5px'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                      borderColor: isCompanyLinked ? 'success.main' : 'primary.main',
                      borderWidth: '2px'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '14px 14px',
                      fontSize: '1rem'
                    }
                  },
                }}
                InputProps={{
                  startAdornment: isCompanyLinked ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        bgcolor: 'success.main', 
                        color: 'white',
                        borderRadius: '8px',
                        width: 30,
                        height: 30,
                        mr: 1.5,
                        ml: 0.5
                      }}
                    >
                      <CheckRounded sx={{ fontSize: 18, fontWeight: 300 }} />
                    </Box>
                  ) : (
                    <InfoRounded sx={{ color: theme.palette.text.secondary, ml: 1, mr: 1 }} />
                  ),
                  endAdornment: isCompanyLinked && (
                    <IconButton 
                      onClick={handleReset} 
                      size="small" 
                      sx={{ 
                        color: 'white',
                        bgcolor: 'error.main',
                        width: 24,
                        height: 24,
                        mr: 0.7,
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                    >
                      <ClearRounded sx={{ fontSize: 16, fontWeight: 300 }} />
                    </IconButton>
                  ),
                  sx: {
                    ...(isCompanyLinked && {
                      backgroundColor: `${theme.palette.success.main}1a`,
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'success.main',
                        borderWidth: '1.5px'
                      },
                    }),
                  },
                }}
              />
              
              <Box
                onClick={() => setShowLocationMode(!showLocationMode)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2,
                  cursor: 'pointer',
                  py: 1,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: `${theme.palette.oneqrew.purple}0a`
                  }
                }}
              >
                <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.divider }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {showLocationMode ? '▼' : '▶'} oder Standort eingeben
                </Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.divider }} />
              </Box>

              <Collapse in={showLocationMode}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: '8px',
                    border: '2px dashed rgba(139, 92, 246, 0.3)',
                    backgroundColor: `${theme.palette.secondary.main}0a`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <LocationOnRounded sx={{ color: theme.palette.secondary.main, fontSize: 20, fontWeight: 300 }} />
                    Standort-basierte Suche
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 2,
                      lineHeight: 1.5
                    }}
                  >
                    Für Nutzer ohne GMB-Profil. Die Analyse erfolgt mit Standard-Ranking (Platz 20).
                  </Typography>

                  <Box sx={{ position: 'relative', mb: error && searchMode !== 'location' ? 2 : 0 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={locationInput}
                      onChange={handleLocationInputChange}
                      onFocus={() => {
                        if (locationSuggestions.length > 0) {
                          setShowLocationSuggestions(true);
                        }
                      }}
                      placeholder="Stadt oder PLZ eingeben (z.B. Hannover)..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-input': {
                            padding: '12px 14px',
                            fontSize: '0.95rem'
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <LocationOnRounded sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                        )
                      }}
                    />

                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <Paper
                        elevation={4}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          mt: 0.5,
                          zIndex: 1000,
                          maxHeight: 300,
                          overflowY: 'auto',
                          borderRadius: '8px',
                          border: '1px solid rgba(139, 92, 246, 0.2)'
                        }}
                      >
                        <List disablePadding>
                          {locationSuggestions.map((suggestion, index) => (
                            <ListItem
                              key={suggestion.place_id || index}
                              button
                              onClick={() => handleSelectLocationSuggestion(suggestion)}
                              sx={{
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                  backgroundColor: `${theme.palette.secondary.main}14`
                                },
                                borderBottom: index < locationSuggestions.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                              }}
                            >
                              <LocationOnRounded sx={{ color: theme.palette.secondary.main, mr: 1.5, fontSize: 20, fontWeight: 300 }} />
                              <ListItemText
                                primary={suggestion.description}
                                primaryTypographyProps={{
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  color: theme.palette.text.primary
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>

                  {error && searchMode !== 'location' && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: `${theme.palette.error.main}1a`,
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.error.main,
                          mt: 0.2
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.error.dark,
                          fontWeight: 500,
                          lineHeight: 1.5
                        }}
                      >
                        {error}
                      </Typography>
                    </Box>
                  )}

                  {searchMode === 'location' && (
                    <Button
                      size="small"
                      onClick={() => {
                        setSearchMode('company');
                        setShowLocationMode(false);
                        handleReset();
                      }}
                      sx={{
                        mt: 1.5,
                        textTransform: 'none',
                        color: theme.palette.text.secondary,
                        fontWeight: 500
                      }}
                    >
                      ← Zurück zur Unternehmenssuche
                    </Button>
                  )}
                </Paper>
              </Collapse>

              {!isCompanyLinked && !showLocationMode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${theme.palette.oneqrew.purple}1a`,
                      color: 'primary.main'
                    }}
                  >
                    <InfoRounded sx={{ fontSize: 16, fontWeight: 300 }} />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'normal',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.5
                    }}
                  >
                    Bitte wählen Sie zuerst ein Unternehmen aus, um Keywords für die Ranking-Analyse einzugeben.
                  </Typography>
                </Box>
              )}
              
              {results.length > 0 && !isCompanyLinked && (
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 0.5, 
                    borderRadius: '8px', 
                    maxHeight: 320, 
                    overflowY: 'auto',
                    border: '1px solid rgba(0, 0, 0, 0.04)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)'
                  }}
                >
                  <List sx={{ py: 0.5 }}>
                    {results.map((item) => (
                      <ListItem
                        key={item.place_id}
                        onClick={() => handleSelectFromList(item)}
                        disablePadding
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '8px',
                          m: 0.5,
                          overflow: 'hidden',
                          '&:hover': { 
                            backgroundColor: `${theme.palette.oneqrew.purple}14`,
                          },
                          transition: 'all 0.15s ease-in-out',
                        }}
                      >
                        <Box sx={{ display: 'flex', width: '100%', px: 2, py: 1.5, alignItems: 'center' }}>
                          <Box 
                            sx={{ 
                              minWidth: 40, 
                              height: 40, 
                              borderRadius: '8px', 
                              backgroundColor: `${theme.palette.oneqrew.purple}1a`, 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <LocationOnRounded sx={{ color: 'primary.main', fontSize: 20, fontWeight: 300 }} />
                          </Box>
                          <ListItemText 
                            primary={item.description.split(',')[0]} 
                            secondary={item.description.split(',').slice(1).join(',')}
                            primaryTypographyProps={{
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              fontSize: '0.95rem'
                            }}
                            secondaryTypographyProps={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              lineHeight: 1.4,
                              mt: 0.5
                            }}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {selected && !isCompanyLinked ? (
                <Box>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      mb: 2,
                      mt: 1
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: theme.palette.oneqrew.purple,
                        color: 'white'
                      }}
                    >
                      <LocationOnRounded sx={{ fontSize: 20, fontWeight: 300 }} />
                    </Box>
                    <span>Ausgewählter Standort</span>
                  </Typography>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      minHeight: 380, 
                      borderRadius: '8px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(0, 0, 0, 0.04)',
                    }}
                    className="hover-scale"
                  >
                    {loadingDetails ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          height: 380,
                          gap: 2
                        }}
                      >
                        <CircularProgress size={48} sx={{ color: theme.palette.oneqrew.purple }} />
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                          Daten werden geladen...
                        </Typography>
                      </Box>
                    ) : details ? (
                      <Box>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={photoUrl}
                            alt={details.name}
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/600x200?text=Kein+Bild+verfügbar';
                              setError('Fehler beim Laden des Bildes.');
                            }}
                            sx={{ 
                              objectFit: 'cover',
                              filter: 'brightness(0.9)',
                            }}
                          />
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              bottom: 0, 
                              left: 0, 
                              right: 0, 
                              p: 2,
                              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                              color: 'white'
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                              {details.name}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                              {details.formatted_address.split(',')[0]}
                            </Typography>
                          </Box>
                        </Box>
                        {error && (
                          <Box sx={{ bgcolor: 'error.light', p: 1.5, borderLeft: '4px solid', borderColor: 'error.main' }}>
                            <Typography variant="body2" color="error.dark">
                              {error}
                            </Typography>
                          </Box>
                        )}
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box 
                                sx={{ 
                                  color: theme.palette.text.secondary, 
                                  mt: 0.5,
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 24,
                                  height: 24
                                }}
                              >
                                <LocationOnRounded fontSize="small" />
                              </Box>
                              <Typography variant="body2" color={theme.palette.text.primary} sx={{ lineHeight: 1.5 }}>
                                {details.formatted_address}
                              </Typography>
                            </Box>
                            {details.international_phone_number && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box 
                                  sx={{ 
                                    color: theme.palette.text.secondary, 
                                    mt: 0.5,
                                    display: 'flex', 
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                                </Box>
                                <Typography variant="body2" color={theme.palette.text.primary} sx={{ lineHeight: 1.5 }}>
                                  {details.international_phone_number}
                                </Typography>
                              </Box>
                            )}
                            {details.website && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box 
                                  sx={{ 
                                    color: theme.palette.text.secondary, 
                                    mt: 0.5,
                                    display: 'flex', 
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                                </Box>
                                <Link 
                                  href={details.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: 'primary.main', 
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: 'primary.dark'
                                    }
                                  }}
                                >
                                  {details.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </Link>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button 
                              variant="contained" 
                              color="primary" 
                              onClick={() => {
                                linkCompany(details);
                              }}
                              sx={{ 
                                borderRadius: '8px',
                                py: 1.2,
                                px: 3,
                                fontWeight: 500,
                                backgroundColor: theme.palette.oneqrew.purple, 
                                '&:hover': { 
                                  backgroundColor: theme.palette.primary.dark,
                                  transform: 'translateY(-2px)',
                                },
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                boxShadow: `0 4px 6px -1px ${theme.palette.oneqrew.purple}33, 0 2px 4px -1px ${theme.palette.oneqrew.purple}0f`
                              }}
                            >
                              Unternehmen auswählen und analysieren
                            </Button>
                          </Box>
                        </CardContent>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 380, 
                        p: 3,
                        flexDirection: 'column'
                      }}>
                        <Box 
                          sx={{ 
                            width: 60, 
                            height: 60, 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: `${theme.palette.oneqrew.purple}1a`, 
                            color: 'primary.main',
                            mb: 2
                          }}
                        >
                          <LocationOnRounded sx={{ fontSize: 30, fontWeight: 300 }} />
                        </Box>
                        <Typography variant="body1" color={theme.palette.text.secondary} textAlign="center" sx={{ mb: 1 }}>
                          Wählen Sie einen Ort aus der Liste aus
                        </Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary} textAlign="center">
                          Hier werden detaillierte Informationen angezeigt
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Box>
              ) : isCompanyLinked ? (
                <Box>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      mb: 2,
                      mt: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: searchMode === 'location' ? theme.palette.secondary.main : theme.palette.success.main,
                        color: 'white'
                      }}
                    >
                      {searchMode === 'location' ? (
                        <LocationOnRounded sx={{ fontSize: 20, fontWeight: 300 }} />
                      ) : (
                        <CheckRounded sx={{ fontSize: 20, fontWeight: 300 }} />
                      )}
                    </Box>
                    <span>{searchMode === 'location' ? 'Standort-Modus' : 'Verknüpftes Unternehmen'}</span>
                  </Typography>
                  <Card
                    elevation={2}
                    sx={{
                      minHeight: 380,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: searchMode === 'location' ? `1px solid ${theme.palette.secondary.main}20` : `1px solid ${theme.palette.success.main}20`,
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                    className="hover-scale"
                  >
                    <Box>
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={photoUrl}
                          alt={details?.name || 'Unternehmen'}
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/600x200?text=Kein+Bild+verfügbar';
                            setError('Fehler beim Laden des Bildes.');
                          }}
                          sx={{ 
                            objectFit: 'cover',
                            filter: 'brightness(0.9)'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: searchMode === 'location' ? 'rgba(139, 92, 246, 0.9)' : 'rgba(16, 185, 129, 0.9)',
                            color: 'white',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}
                        >
                          {searchMode === 'location' ? (
                            <>
                              <LocationOnRounded sx={{ fontSize: 16, fontWeight: 300 }} />
                              Standort
                            </>
                          ) : (
                            <>
                              <CheckRounded fontSize="small" />
                              Aktiv
                            </>
                          )}
                        </Box>
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            p: 2,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                            color: 'white'
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                            {details?.name || 'Unternehmen'}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {details?.formatted_address?.split(',')[0] || ''}
                          </Typography>
                        </Box>
                      </Box>
                      {error && (
                        <Box sx={{ bgcolor: 'error.light', p: 1.5, borderLeft: '4px solid', borderColor: 'error.main' }}>
                          <Typography variant="body2" color="error.dark">
                            {error}
                          </Typography>
                        </Box>
                      )}
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box 
                              sx={{ 
                                color: theme.palette.text.secondary, 
                                mt: 0.5,
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24
                              }}
                            >
                              <LocationOnRounded fontSize="small" />
                            </Box>
                            <Typography variant="body2" color={theme.palette.text.primary} sx={{ lineHeight: 1.5 }}>
                              {details?.formatted_address || ''}
                            </Typography>
                          </Box>
                          {details?.international_phone_number && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box 
                                sx={{ 
                                  color: theme.palette.text.secondary, 
                                  mt: 0.5,
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 24,
                                  height: 24
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                              </Box>
                              <Typography variant="body2" color={theme.palette.text.primary} sx={{ lineHeight: 1.5 }}>
                                {details.international_phone_number}
                              </Typography>
                            </Box>
                          )}
                          {details?.website && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box 
                                sx={{ 
                                  color: theme.palette.text.secondary, 
                                  mt: 0.5,
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 24,
                                  height: 24
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                              </Box>
                              <Link 
                                href={details.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'primary.main', 
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  '&:hover': { 
                                    textDecoration: 'underline',
                                    color: 'primary.dark'
                                  }
                                }}
                              >
                                {details.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </Link>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                          <Button 
                            variant="outlined"  
                            onClick={handleReset}
                            startIcon={<ClearRounded />}
                            sx={{ 
                              borderRadius: '8px',
                              py: 1,
                              px: 2.5,
                              fontWeight: 500,
                              borderColor: theme.palette.error.light,
                              color: theme.palette.error.main,
                              '&:hover': { 
                                borderColor: theme.palette.error.main,
                                backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                transform: 'translateY(-2px)',
                              },
                              textTransform: 'none',
                              fontSize: '0.9rem'
                            }}
                          >
                            Verknüpfung aufheben
                          </Button>
                        </Box>
                      </CardContent>
                    </Box>
                  </Card>
                </Box>
              ) : null}
            </Grid>
          </Grid>
        </Paper>

        {isCompanyLinked && details && (
          <Paper 
            elevation={2} 
            sx={{ 
              mt: 5, 
              width: '100%',
              borderRadius: '8px',
              background: 'white',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1, 
                      gap: 1.5,
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: theme.palette.oneqrew.purple,
                        color: 'white'
                      }}
                    >
                      <BarChartRounded />
                    </Box>
                    <span>Ranking-Analyse</span>
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: theme.palette.text.secondary, 
                      fontWeight: 400,
                      pl: 0.5
                    }}
                  >
                    für <span style={{ fontWeight: 500, color: theme.palette.text.primary }}>{details.name}</span>
                  </Typography>
                </Box>
              </Box>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3.5, 
                mb: 4, 
                borderRadius: '8px', 
                border: '1px solid rgba(99, 102, 241, 0.08)',
                background: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)'
              }}
            >
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  color: theme.palette.text.primary,
                  mb: 2.5
                }}
              >
                <Box 
                  sx={{ 
                    width: 28, 
                    height: 28, 
                    borderRadius: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: `${theme.palette.oneqrew.purple}1a`,
                    color: theme.palette.oneqrew.purple
                  }}
                >
                  <BarChartRounded sx={{ fontSize: 18, fontWeight: 300 }} />
                </Box>
                Keyword-Analyse
              </Typography>
              
              {!searchKeyword && (
                <Box sx={{ 
                  py: 4, 
                  px: 3,
                  mb: 3, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: `${theme.palette.oneqrew.purple}0a`,
                  borderRadius: '8px',
                  border: '1px dashed rgba(99, 102, 241, 0.2)'
                }}>
                  <Box 
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: `${theme.palette.oneqrew.purple}1a`, 
                      color: theme.palette.oneqrew.purple,
                      mb: 2.5
                    }}
                  >
                    <BarChartRounded sx={{ fontSize: 30, fontWeight: 300 }} />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      textAlign: 'center', 
                      mb: 1.5, 
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    }}
                  >
                    Geben Sie ein Keyword ein
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      textAlign: 'center',
                      color: theme.palette.text.secondary,
                      maxWidth: 400,
                      mx: 'auto'
                    }}
                  >
                    Geben Sie ein Schlüsselwort ein, um zu analysieren, wie Ihr Unternehmen für unterschiedliche Suchbegriffe rankt.
                  </Typography>
                </Box>
              )}

              {/* Multi-Keyword Mode Toggle */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant={isMultiKeywordMode ? "contained" : "outlined"}
                  onClick={() => setIsMultiKeywordMode(!isMultiKeywordMode)}
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    // Remove default focus outline, use only focus-visible
                    '&:focus': {
                      outline: 'none',
                    },
                    '&:focus-visible': {
                      outline: '2px solid #C534FF',
                      outlineOffset: '2px',
                    },
                    ...(isMultiKeywordMode ? {
                      bgcolor: theme.palette.oneqrew.primaryContainerAlt,
                      color: theme.palette.oneqrew.primaryContainer,
                      '&:hover': {
                        bgcolor: theme.palette.oneqrew.primaryContainerAlt,
                        filter: 'brightness(0.95)'
                      }
                    } : {
                      borderColor: theme.palette.oneqrew.primaryContainer,
                      color: theme.palette.oneqrew.primaryContainer,
                      borderWidth: '2px',
                      '&:hover': {
                        borderColor: theme.palette.oneqrew.primaryContainer,
                        bgcolor: `${theme.palette.oneqrew.primaryContainer}0a`
                      }
                    })
                  }}
                >
                  {isMultiKeywordMode ? '✓ Multi-Keyword Modus' : 'Multi-Keyword Modus aktivieren'}
                </Button>
              </Box>

              {/* Multi-Keyword Input Section */}
              {isMultiKeywordMode && (
                <Box sx={{ mb: 3 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: '8px',
                      border: '2px solid rgba(139, 92, 246, 0.3)',
                      backgroundColor: `${theme.palette.secondary.main}0a`
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <BarChartRounded sx={{ color: theme.palette.oneqrew.purple, fontSize: 20, fontWeight: 300 }} />
                      Mehrere Keywords gleichzeitig analysieren
                    </Typography>

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={multiKeywordInput}
                      onChange={(e) => setMultiKeywordInput(e.target.value)}
                      placeholder="Keywords eingeben (durch Komma, Semikolon oder Zeilenumbruch getrennt)&#10;Beispiel:&#10;PV Anlage, Restaurant, Gartenbau"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-input': {
                            fontSize: '0.95rem',
                            lineHeight: 1.6
                          }
                        }
                      }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip
                          label={`${parsedKeywords.length} Keyword${parsedKeywords.length !== 1 ? 's' : ''} erkannt`}
                          size="small"
                          sx={{
                            bgcolor: parsedKeywords.length > 0 ? 'rgba(25, 0, 35, 0.08)' : 'rgba(148, 163, 184, 0.15)',
                            color: parsedKeywords.length > 0 ? theme.palette.oneqrew.purple : theme.palette.text.secondary,
                            fontWeight: 500
                          }}
                        />
                        {parsedKeywords.length > 0 && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Max. {simultaneousKeywords} parallel
                          </Typography>
                        )}
                      </Box>

                      <Button
                        variant="contained"
                        onClick={analyzeMultipleKeywords}
                        disabled={parsedKeywords.length === 0 || isLoadingKeyword}
                        sx={{
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          fontWeight: 500,
                          bgcolor: theme.palette.oneqrew.purple,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark
                          },
                          textTransform: 'none'
                        }}
                      >
                        Alle {parsedKeywords.length} Keywords analysieren
                      </Button>
                    </Box>

                    {parsedKeywords.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {parsedKeywords.map((kw, index) => (
                          <Chip
                            key={index}
                            label={kw}
                            size="small"
                            sx={{
                              bgcolor: 'white',
                              border: `1px solid ${theme.palette.divider}`,
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>

                  {/* Keyword Status Cards Grid */}
                  {activeKeywords.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <BarChartRounded sx={{ color: theme.palette.secondary.main }} />
                        Analyse-Status
                      </Typography>

                      <Grid container spacing={2}>
                        {activeKeywords.map((kw, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                              onClick={() => {
                                if (kw.status === 'complete' && kw.result) {
                                  displayKeywordResults(kw.keyword, kw.result);
                                }
                              }}
                              sx={{
                                cursor: kw.status === 'complete' ? 'pointer' : 'default',
                                borderRadius: '8px',
                                border: selectedKeywordForView === kw.keyword
                                  ? `2px solid ${theme.palette.secondary.main}`
                                  : `1px solid ${theme.palette.divider}`,
                                transition: 'all 0.2s ease',
                                '&:hover': kw.status === 'complete' ? {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 16px rgba(139, 92, 246, 0.2)'
                                } : {},
                                bgcolor: selectedKeywordForView === kw.keyword ? `${theme.palette.secondary.main}0a` : 'white'
                              }}
                            >
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 500,
                                      color: theme.palette.text.primary,
                                      fontSize: '1rem'
                                    }}
                                  >
                                    {kw.keyword}
                                  </Typography>
                                  <Chip
                                    label={
                                      kw.status === 'pending' ? 'Wartend' :
                                      kw.status === 'analyzing' ? 'Analysiert...' :
                                      kw.status === 'complete' ? 'Fertig' :
                                      'Fehler'
                                    }
                                    size="small"
                                    sx={{
                                      fontWeight: 500,
                                      ...(kw.status === 'pending' && {
                                        bgcolor: 'rgba(148, 163, 184, 0.15)',
                                        color: theme.palette.text.secondary
                                      }),
                                      ...(kw.status === 'analyzing' && {
                                        bgcolor: 'rgba(99, 102, 241, 0.15)',
                                        color: theme.palette.oneqrew.purple
                                      }),
                                      ...(kw.status === 'complete' && {
                                        bgcolor: `${theme.palette.success.main}26`,
                                        color: theme.palette.success.main
                                      }),
                                      ...(kw.status === 'error' && {
                                        bgcolor: 'rgba(239, 68, 68, 0.15)',
                                        color: theme.palette.error.main
                                      })
                                    }}
                                  />
                                </Box>

                                {kw.status === 'analyzing' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CircularProgress size={16} sx={{ color: theme.palette.oneqrew.purple }} />
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                      Wird analysiert...
                                    </Typography>
                                  </Box>
                                )}

                                {kw.status === 'complete' && kw.result && (
                                  <Box sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                        Durchschn. Rang:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          color: kw.result.averageRank <= 3 ? theme.palette.success.main :
                                                 kw.result.averageRank <= 10 ? theme.palette.warning.main : theme.palette.error.main
                                        }}
                                      >
                                        {kw.result.averageRank ? kw.result.averageRank.toFixed(1) : 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                        Bester Rang:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          color: theme.palette.success.main
                                        }}
                                      >
                                        {kw.result.bestRank || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                        Gerankte Standorte:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        {kw.result.rankedLocations || 0}/18
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}

                                {kw.status === 'error' && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: theme.palette.error.main,
                                        fontSize: '0.85rem',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {kw.error || 'Unbekannter Fehler'}
                                    </Typography>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}

              {/* Single Keyword Input Section */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center' }}>
                <TextField
                  variant="outlined"
                  value={searchKeywordInput}
                  onChange={(e) => setSearchKeywordInput(e.target.value)}
                  placeholder="Keyword eingeben (z.B. PV Anlage, Restaurant, Gartenbau)"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      borderColor: theme.palette.divider,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 14px',
                        fontSize: '0.95rem'
                      },
                      '&:hover': {
                        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.08)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: theme.palette.divider,
                        borderWidth: '1px'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                        borderColor: theme.palette.oneqrew.purple,
                        borderWidth: '1.5px'
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box 
                        sx={{ 
                          color: theme.palette.text.secondary, 
                          display: 'flex', 
                          alignItems: 'center',
                          ml: 0.5,
                          mr: 1
                        }}
                      >
                        <InfoRounded fontSize="small" />
                      </Box>
                    ),
                  }}
                />
                <Button 
                  variant="contained" 
                  disabled={isLoadingKeyword || !searchKeywordInput.trim()} 
                  onClick={() => fetchLocationRanks(details, searchKeywordInput.trim())}
                  sx={{ 
                    minWidth: '150px',
                    borderRadius: '8px',
                    py: 1.5,
                    fontWeight: 500,
                    bgcolor: theme.palette.oneqrew.purple,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)'
                    },
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    boxShadow: `0 4px 6px -1px ${theme.palette.oneqrew.purple}33, 0 2px 4px -1px ${theme.palette.oneqrew.purple}0f`
                  }}
                >
                  {isLoadingKeyword ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: 'white' }} />
                      <span>Analysiere...</span>
                    </Box>
                  ) : (
                    "Keyword analysieren"
                  )}
                </Button>
              </Box>
              
              {keywordError && (
                <Box 
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: `${theme.palette.error.light}33`,
                      color: theme.palette.error.main,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5
                    }}
                  >
                    <ClearRounded sx={{ fontSize: 16, fontWeight: 300 }} />
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.error.dark,
                      fontWeight: 500,
                      lineHeight: 1.5
                    }}
                  >
                    {keywordError}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1.5, 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.oneqrew.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                  Aktive Keywords:
                </Typography>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: theme.palette.background.default
                  }}
                >
                  {keywordsHistory.length > 0 ? (
                    keywordsHistory.map((keyword, index) => (
                      <Chip
                        key={index}
                        label={keyword}
                        onClick={() => fetchLocationRanks(details, keyword)}
                        onDelete={() => {
                          setKeywordsHistory(prev => prev.filter(k => k !== keyword));
                          if (keyword === searchKeyword && keywordsHistory.length > 1) {
                            const newKeyword = keywordsHistory.find(k => k !== keyword);
                            fetchLocationRanks(details, newKeyword);
                          }
                        }}
                        sx={{ 
                          fontWeight: keyword === searchKeyword ? 600 : 500,
                          '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
                          px: 1,
                          height: 32,
                          borderRadius: '8px',
                          ...(keyword === searchKeyword ? {
                            bgcolor: theme.palette.oneqrew.purple,
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              opacity: 0.8,
                              '&:hover': {
                                opacity: 1
                              }
                            }
                          } : {
                            bgcolor: 'white',
                            color: theme.palette.text.secondary,
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              bgcolor: theme.palette.background.default
                            },
                            '& .MuiChip-deleteIcon': {
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                color: theme.palette.error.main
                              }
                            }
                          })
                        }}
                      />
                    ))
                  ) : (
                    <Box 
                      sx={{ 
                        p: 3,
                        width: '100%',
                        textAlign: 'center',
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: `1px dashed ${theme.palette.divider}`,
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.secondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                        Keine Keywords vorhanden. Geben Sie oben ein Keyword ein, um zu beginnen.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
            
            <>
                {searchKeyword ? (
                  <Box 
                    sx={{ 
                      mb: 3, 
                      py: 2, 
                      px: 3, 
                      borderRadius: '8px', 
                      bgcolor: 'rgba(99, 102, 241, 0.05)',
                      border: '1px solid rgba(99, 102, 241, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Box 
                      sx={{ 
                        minWidth: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: `${theme.palette.oneqrew.purple}1a`, 
                        color: theme.palette.oneqrew.purple,
                      }}
                    >
                      <InfoRounded sx={{ fontSize: 22, fontWeight: 300 }} />
                    </Box>
                    <Typography 
                      sx={{ 
                        color: theme.palette.text.primary,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        lineHeight: 1.5
                      }}
                    >
                      Die Karte zeigt Ihre Platzierung für "<span style={{ fontWeight: 500, color: theme.palette.text.primary }}>{searchKeyword}</span>" an verschiedenen Standorten.
                      <span style={{ color: theme.palette.success.main, fontWeight: 500 }}> Grüne Marker (1-3)</span> zeigen Top-Platzierungen,
                      <span style={{ color: theme.palette.warning.main, fontWeight: 500 }}> orangefarbene (4-10)</span> gute Platzierungen und
                      <span style={{ color: theme.palette.error.main, fontWeight: 500 }}> rote (11+)</span> niedrigere Platzierungen.
                    </Typography>
                  </Box>
                ) : null}
                
                {isLoadingKeyword ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: 500,
                      borderRadius: '8px',
                      bgcolor: 'white',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress 
                        size={70} 
                        sx={{ 
                          mb: 3,
                          color: theme.palette.oneqrew.purple
                        }} 
                      />
                      <Typography 
                        variant="h4"
                        sx={{ 
                          mb: 1.5,
                          fontWeight: 500,
                          color: theme.palette.text.primary
                        }}
                      >
                        Analysiere "{searchKeyword}"
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          maxWidth: 450,
                          mx: 'auto'
                        }}
                      >
                        Die Daten für verschiedene Standorte werden gesammelt und analysiert. Dies kann einen Moment dauern...
                      </Typography>
                    </Box>
                  </Box>
                ) : searchKeyword ? (
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <LocationMap
                      companyLocation={{ ...details.geometry.location, placeId: details.place_id }}
                      companyName={details.name}
                      locationRanks={locationRanks}
                      keywordVolume={keywordVolume}
                      searchKeyword={searchKeyword}
                      topCompetitors={topCompetitors}
                      isLocationMode={searchMode === 'location'}
                    />
                  </Paper>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 500, 
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: `1px dashed ${theme.palette.divider}`,
                    flexDirection: 'column'
                  }}>
                    <Box 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: `${theme.palette.oneqrew.purple}1a`, 
                        color: theme.palette.oneqrew.purple,
                        mb: 3
                      }}
                    >
                      <MapRounded sx={{ fontSize: 40, fontWeight: 300 }} />
                    </Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: theme.palette.text.primary, 
                        mb: 1,
                        fontWeight: 500
                      }}
                    >
                      Ranking-Karte
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        maxWidth: 450,
                        textAlign: 'center',
                        lineHeight: 1.6
                      }}
                    >
                      Geben Sie ein Keyword ein und starten Sie die Analyse, um eine interaktive Karte mit den Ranking-Positionen Ihres Unternehmens zu sehen.
                    </Typography>
                  </Box>
                )}

                {/* CTA Section - Show when rankings need improvement */}
                {searchKeyword && locationRanks.length > 0 && hasImprovableRankings() && (
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 4,
                      p: 4,
                      borderRadius: '24px',
                      background: `linear-gradient(135deg, ${theme.palette.oneqrew.purple} 0%, #2D1937 100%)`,
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Background Pattern with Gradient Fade */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0.1,
                        background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                        maskImage: `linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)`,
                        WebkitMaskImage: `linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)`
                      }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      {!showCTAForm ? (
                        <>
                          {/* CTA Content */}
                          <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center' }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 500,
                                mb: 2,
                                color: 'white',
                                fontSize: { xs: '28px', md: '36px' }
                              }}
                            >
                              Ihre Rankings haben Potenzial nach oben!
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                mb: 4,
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 400,
                                lineHeight: 1.6,
                                fontSize: { xs: '18px', md: '20px' }
                              }}
                            >
                              Als Handwerksbetrieb verlieren Sie täglich Aufträge an besser platzierte Wettbewerber.
                              Wir helfen Ihnen, in Ihrer Region ganz vorne zu stehen – wo Ihre Kunden Sie finden.
                            </Typography>

                            {/* Benefits */}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 3,
                                mb: 4
                              }}
                            >
                              {[
                                { icon: '📈', title: 'Mehr Sichtbarkeit', desc: 'Top-Platzierungen in Ihrer Region' },
                                { icon: '🎯', title: 'Mehr Anfragen', desc: 'Qualifizierte Leads direkt zu Ihnen' },
                                { icon: '💰', title: 'Mehr Umsatz', desc: 'Konstanter Auftragsstrom ohne Akquise' }
                              ].map((benefit, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    p: 3,
                                    borderRadius: '16px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                  }}
                                >
                                  <Typography sx={{ fontSize: '32px', mb: 1 }}>{benefit.icon}</Typography>
                                  <Typography sx={{ fontWeight: 500, mb: 0.5, fontSize: '18px' }}>
                                    {benefit.title}
                                  </Typography>
                                  <Typography sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                                    {benefit.desc}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            <Button
                              variant="contained"
                              size="large"
                              onClick={() => setShowCTAForm(true)}
                              sx={{
                                bgcolor: theme.palette.oneqrew.yellow,
                                color: theme.palette.oneqrew.purple,
                                px: 5,
                                py: 2,
                                fontSize: '18px',
                                fontWeight: 600,
                                borderRadius: '12px',
                                textTransform: 'none',
                                boxShadow: '0 8px 20px rgba(240, 245, 95, 0.3)',
                                '&:hover': {
                                  bgcolor: theme.palette.oneqrew.yellow,
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 12px 28px rgba(240, 245, 95, 0.4)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              Jetzt kostenlose Beratung sichern →
                            </Button>

                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 2,
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '13px'
                              }}
                            >
                              ✓ Unverbindlich & kostenlos  ✓ Individuelle Strategie  ✓ Keine versteckten Kosten
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <>
                          {/* Contact Form */}
                          {!ctaSubmitSuccess ? (
                            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 500,
                                  mb: 1,
                                  textAlign: 'center',
                                  color: 'white'
                                }}
                              >
                                Starten Sie jetzt durch!
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  mb: 3,
                                  textAlign: 'center',
                                  color: 'rgba(255, 255, 255, 0.9)'
                                }}
                              >
                                Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                              </Typography>

                              <form onSubmit={handleCTASubmit}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <TextField
                                    required
                                    fullWidth
                                    label="Ihr Name"
                                    value={ctaFormData.name}
                                    onChange={(e) => setCtaFormData({ ...ctaFormData, name: e.target.value })}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: '8px'
                                      }
                                    }}
                                  />
                                  <TextField
                                    required
                                    fullWidth
                                    label="Firma"
                                    value={ctaFormData.company}
                                    onChange={(e) => setCtaFormData({ ...ctaFormData, company: e.target.value })}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: '8px'
                                      }
                                    }}
                                  />
                                  <TextField
                                    required
                                    fullWidth
                                    type="tel"
                                    label="Telefon"
                                    value={ctaFormData.phone}
                                    onChange={(e) => setCtaFormData({ ...ctaFormData, phone: e.target.value })}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: '8px'
                                      }
                                    }}
                                  />
                                  <TextField
                                    required
                                    fullWidth
                                    type="email"
                                    label="E-Mail"
                                    value={ctaFormData.email}
                                    onChange={(e) => setCtaFormData({ ...ctaFormData, email: e.target.value })}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: 'white',
                                        borderRadius: '8px'
                                      }
                                    }}
                                  />

                                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                    <Button
                                      fullWidth
                                      variant="outlined"
                                      onClick={() => setShowCTAForm(false)}
                                      disabled={isSubmittingCTA}
                                      sx={{
                                        color: 'white',
                                        borderColor: 'white',
                                        borderWidth: '2px',
                                        borderRadius: '8px',
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        '&:hover': {
                                          borderColor: 'white',
                                          borderWidth: '2px',
                                          bgcolor: 'rgba(255, 255, 255, 0.15)'
                                        }
                                      }}
                                    >
                                      Zurück
                                    </Button>
                                    <Button
                                      fullWidth
                                      type="submit"
                                      variant="contained"
                                      disabled={isSubmittingCTA}
                                      sx={{
                                        bgcolor: theme.palette.oneqrew.yellow,
                                        color: theme.palette.oneqrew.purple,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        '&:hover': {
                                          bgcolor: theme.palette.oneqrew.yellow,
                                          transform: 'translateY(-1px)'
                                        }
                                      }}
                                    >
                                      {isSubmittingCTA ? (
                                        <CircularProgress size={24} sx={{ color: theme.palette.oneqrew.purple }} />
                                      ) : (
                                        'Jetzt Beratung anfordern'
                                      )}
                                    </Button>
                                  </Box>
                                </Box>
                              </form>
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <CheckRounded sx={{ fontSize: 64, color: theme.palette.oneqrew.yellow, mb: 2 }} />
                              <Typography variant="h4" sx={{ fontWeight: 500, mb: 1, color: 'white' }}>
                                Vielen Dank!
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                Wir haben Ihre Anfrage erhalten und melden uns in Kürze bei Ihnen.
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Paper>
                )}
              </>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Exit-Intent Modal - High-Converting Last Chance CTA */}
      <Dialog
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${theme.palette.oneqrew.purple} 0%, #2D1937 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            p: 0
          }
        }}
      >
        {/* Background Pattern with Gradient Fade */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.08,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            maskImage: `linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)`,
            WebkitMaskImage: `linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)`
          }}
        />

        <DialogContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              label="Kostenfreie Potenzialanalyse"
              sx={{
                bgcolor: theme.palette.oneqrew.yellow,
                color: theme.palette.oneqrew.purple,
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
                height: 'auto',
                '& .MuiChip-label': {
                  px: 1,
                  py: 1
                }
              }}
            />
          </Box>

          {/* Headline */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              fontSize: { xs: '1.75rem', sm: '2rem' }
            }}
          >
            Moment noch!
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              textAlign: 'center',
              fontSize: '1.125rem',
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            Sie haben gerade gesehen, wo Ihr Ranking Potenzial nach oben hat.
            Wir helfen Ihnen dabei, diese Chancen zu nutzen.
          </Typography>

          {/* Benefits */}
          <Box sx={{ mb: 3 }}>
            {[
              { icon: '📈', title: 'Mehr Sichtbarkeit', text: 'Top-Platzierungen in Ihrer Region' },
              { icon: '🎯', title: 'Mehr Anfragen', text: 'Qualifizierte Leads aus Google' },
              { icon: '💰', title: 'Mehr Umsatz', text: 'Jeden Monat neue Aufträge' }
            ].map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography sx={{ fontSize: '1.5rem' }}>{benefit.icon}</Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {benefit.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* CTA Form */}
          {!ctaSubmitSuccess ? (
            <Box component="form" onSubmit={handleCTASubmit}>
              <TextField
                fullWidth
                required
                placeholder="Ihr Name *"
                value={ctaFormData.name}
                onChange={(e) => setCtaFormData({ ...ctaFormData, name: e.target.value })}
                disabled={isSubmittingCTA}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: 'transparent' }
                  }
                }}
              />

              <TextField
                fullWidth
                required
                placeholder="Firma *"
                value={ctaFormData.company}
                onChange={(e) => setCtaFormData({ ...ctaFormData, company: e.target.value })}
                disabled={isSubmittingCTA}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: 'transparent' }
                  }
                }}
              />

              <TextField
                fullWidth
                required
                type="tel"
                placeholder="Telefon *"
                value={ctaFormData.phone}
                onChange={(e) => setCtaFormData({ ...ctaFormData, phone: e.target.value })}
                disabled={isSubmittingCTA}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: 'transparent' }
                  }
                }}
              />

              <TextField
                fullWidth
                required
                type="email"
                placeholder="E-Mail *"
                value={ctaFormData.email}
                onChange={(e) => setCtaFormData({ ...ctaFormData, email: e.target.value })}
                disabled={isSubmittingCTA}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: 'transparent' }
                  }
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmittingCTA}
                sx={{
                  bgcolor: theme.palette.oneqrew.yellow,
                  color: theme.palette.oneqrew.purple,
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1.5,
                  borderRadius: '8px',
                  mb: 2,
                  '&:hover': {
                    bgcolor: '#F8F870'
                  },
                  textTransform: 'none'
                }}
              >
                {isSubmittingCTA ? 'Wird gesendet...' : 'Jetzt kostenlose Beratung sichern'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => setShowExitModal(false)}
                disabled={isSubmittingCTA}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  },
                  textTransform: 'none'
                }}
              >
                Vielleicht später
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 500, mb: 2 }}>
                ✓ Vielen Dank!
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Wir melden uns innerhalb von 24 Stunden bei Ihnen.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default App;